import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EBenefitTypes } from "../enums/benefits.enums";
import { CompanyBenefitDto } from "../types/benefits.types";
import { normalizeBenefits, recomputeUsage } from "./benefits.helper";
import { initialState } from "./benefits.initialState";
import { fetchBenefits } from "./benefits.thunks";
import { resetUser } from "../../../redux/features/userStateSlice";

export const benefitsSlice = createSlice({
  name: "benefits",
  initialState,
  reducers: {
    setCompanyBenefits(state, action: PayloadAction<CompanyBenefitDto[]>) {
      state.byType = normalizeBenefits(action.payload);
      state.status = "succeeded";
      state.error = undefined;
      state.loadedAt = new Date().toISOString();
    },

    resetBenefits(state) {
      state.byType = {};
      state.status = "idle";
      state.error = undefined;
      state.loadedAt = undefined;
      state.subscriptions = undefined;
    },

    consumeBenefitOptimistic(state, action: PayloadAction<{ type: EBenefitTypes; amount?: number }>) {
      const { type, amount = 1 } = action.payload;
      const ent = state.byType[type];
      if (!ent) return;

      const safeAmount = Math.max(1, amount);
      ent.used = Math.max(0, ent.used + safeAmount);

      recomputeUsage(ent);
    },

    syncUsageFromServer(state, action: PayloadAction<{ type: EBenefitTypes; usageCount: number; expDate?: string }>) {
      const { type, usageCount, expDate } = action.payload;
      const ent = state.byType[type];
      if (!ent) return;

      ent.used = Math.max(0, usageCount);
      if (typeof expDate === "string") ent.expDate = expDate;

      recomputeUsage(ent);
    },
    setSubscriptions(state, action: PayloadAction<{ subscription: any }>) {
      state.subscriptions = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetUser, (state) => {
        state.byType = {};
        state.status = "idle";
        state.error = undefined;
        state.loadedAt = undefined;
        state.subscriptions = undefined;
      })
      .addCase(fetchBenefits.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(fetchBenefits.fulfilled, (state, action) => {
        state.byType = normalizeBenefits(action.payload);
        state.status = "succeeded";
        state.error = undefined;
        state.loadedAt = new Date().toISOString();
      })
      .addCase(fetchBenefits.rejected, (state, action) => {
        state.status = "failed";
        state.error = (action.payload as string) ?? action.error.message ?? "Failed";
      });
  },
});

export const { setCompanyBenefits, resetBenefits, consumeBenefitOptimistic, syncUsageFromServer, setSubscriptions } = benefitsSlice.actions;

export default benefitsSlice.reducer;
